import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { API } from "../lib/api";
import useNativeTelemetry from "../hooks/useNativeTelemetry";
import NotificationService from "../services/NotificationService";

/**
 * Non-visual mount-point that wires up native-only concerns:
 *   • Background GPS via @capacitor-community/background-geolocation
 *     (foreground service on Android, `location` background mode on iOS)
 *   • FCM push token registration + inbound-notification listeners
 *   • Deep-link routing — when the OS launches the app via an App Link
 *     (proximity.life/join/<token> or proximity.life/accept/<code>) we
 *     must forward the incoming URL into React Router; otherwise the
 *     app opens on its last screen and the invitation token is lost.
 *
 * On web this component becomes a pure no-op.
 */
export default function NativeBootstrap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [backgroundLocationEnabled, setBackgroundLocationEnabled] = useState(() => {
    try { return localStorage.getItem("proximity:background-location") === "enabled"; }
    catch { return false; }
  });
  const [foregroundLocationEnabled, setForegroundLocationEnabled] = useState(() => {
    try { return localStorage.getItem("proximity:foreground-location") === "enabled"; }
    catch { return false; }
  });

  // Session token lives in localStorage (see /app/frontend/src/lib/api.js —
  // we switched away from cookies so Capacitor Android WebViews stop being
  // blocked by the platform-level `Access-Control-Allow-Origin: *` combo).
  const sessionToken = (() => {
    try { return localStorage.getItem("session_token"); } catch { return null; }
  })();

  // Native telemetry & FCM registration send to the same origin as
  // /lib/api.js, which already applies the "never localhost/undefined"
  // fallback. Strip the trailing "/api" — telemetry hits root paths.
  const backendUrl = API.replace(/\/api$/, "");

  // Background telemetry (native only — hook self-exits on web)
  useNativeTelemetry(
    user ? sessionToken : null,
    backendUrl,
    foregroundLocationEnabled && user?.share_location !== false,
    backgroundLocationEnabled,
  );

  // Dashboard owns the user-facing explanation. This listener keeps the
  // native background service opt-in separate from foreground location access.
  useEffect(() => {
    const onForegroundChoice = (event) => {
      const enabled = Boolean(event?.detail?.enabled);
      try { localStorage.setItem("proximity:foreground-location", enabled ? "enabled" : "declined"); } catch {}
      setForegroundLocationEnabled(enabled);
    };
    window.addEventListener("proximity:foreground-location", onForegroundChoice);
    return () => window.removeEventListener("proximity:foreground-location", onForegroundChoice);
  }, []);

  useEffect(() => {
    const onBackgroundChoice = (event) => {
      const enabled = Boolean(event?.detail?.enabled);
      try { localStorage.setItem("proximity:background-location", enabled ? "enabled" : "declined"); } catch {}
      if (enabled) {
        try { localStorage.setItem("proximity:foreground-location", "enabled"); } catch {}
        setForegroundLocationEnabled(true);
      }
      setBackgroundLocationEnabled(enabled);
    };
    window.addEventListener("proximity:background-location", onBackgroundChoice);
    return () => window.removeEventListener("proximity:background-location", onBackgroundChoice);
  }, []);

  // FCM registration — once, when the user becomes known
  useEffect(() => {
    if (!user) return;
    if (!Capacitor.isNativePlatform()) return;
    NotificationService.register({ sessionToken, backendUrl });
  }, [user, sessionToken, backendUrl]);

  // Notification actions from Firebase should land directly on the relevant
  // contact instead of leaving the user at an unrelated restored screen.
  useEffect(() => {
    const onOpenAlert = (event) => {
      const contactId = event?.detail?.contactUserId;
      navigate(contactId ? `/dashboard?focus=${encodeURIComponent(contactId)}` : "/alerts");
    };
    window.addEventListener("proximity:open-alert", onOpenAlert);
    return () => window.removeEventListener("proximity:open-alert", onOpenAlert);
  }, [navigate]);

  // App-link / deep-link routing — extract the path (+ query + hash) from an
  // incoming full URL and drive React Router via `location.href` (Capacitor
  // WebViews behave like a fresh browser tab, so a same-origin assignment
  // reloads the SPA to the correct route without a network fetch).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let sub;
    (async () => {
      sub = await CapacitorApp.addListener("appUrlOpen", (event) => {
        try {
          if (!event?.url) return;
          const u = new URL(event.url);
          const target = `${u.pathname}${u.search}${u.hash}`;
          // Only intercept our own domains — leave arbitrary in-app deep
          // links (e.g. Firebase Dynamic Links) alone.
          if (!/^(www\.)?proximity\.life$/i.test(u.hostname) &&
              u.protocol !== "proximitylife:") return;
          if (target && target !== "/") {
            // Persist the token immediately so JoinInvite/AcceptInvite pages
            // survive an auth redirect (SignIn → back to /join/<token>).
            const joinMatch = target.match(/^\/join\/([^/?#]+)/);
            const acceptMatch = target.match(/^\/accept\/([^/?#]+)/);
            if (joinMatch) {
              localStorage.setItem("pending_invite", JSON.stringify({ kind: "contact", token: joinMatch[1] }));
            } else if (acceptMatch) {
              localStorage.setItem("pending_invite", JSON.stringify({ kind: "device", token: acceptMatch[1] }));
            }
            // Route inside the SPA. Using history rather than location.href
            // avoids a full reload for warm-launches.
            if (window.history?.pushState) {
              window.history.pushState({}, "", target);
              window.dispatchEvent(new PopStateEvent("popstate"));
            } else {
              window.location.assign(target);
            }
          }
        } catch { /* malformed URL — ignore */ }
      });
    })();
    return () => { try { sub?.remove?.(); } catch {} };
  }, []);

  return null;
}
